import {
  Checkbox,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Loader2, Package, Search, X } from "lucide-react";
import React from "react";

const ItemListDrawerSalesReturn = ({
  invoiceDetails,
  selectedRowData,
  loadingDetails,
  filterItems,
  drawerOpen,
  handleCloseDrawer,
  itemSearchTerm,
  setItemSearchTerm,
  getUniqueItemId,
  itemQuantities,
  handleQuantityChange,
  calculateLineTotal,
  handleTransfer,
  isAllSelected,
  isSomeSelected,
  handleSelectAll,
  checkedItems,
  hasAnyChecked,
  checkedCount,
  handleCheckboxToggle,
  docNoField = "INVNO",
  customerField = "CUSTOMER",
  isPurchaseReturn = false,
}) => {
  const itemQtyField = isPurchaseReturn ? "tr_qty2" : "tr_qty";
  const itemLineTotalField = isPurchaseReturn ? "fline_tota" : "line_total";
  const details = invoiceDetails[selectedRowData?.[docNoField]] || [];
  const isLoadingDetails = loadingDetails[selectedRowData?.[docNoField]];
  const filteredDetails = filterItems(details);
  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 650 } },
        }}
      >
        <div className="flex flex-col h-full">
          {/* Drawer Header */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Invoice Items
                </h3>
                <p className="text-sm text-blue-100 mt-0.5">
                  {selectedRowData?.[docNoField]} • {selectedRowData?.[customerField]}
                </p>
              </div>
              <IconButton
                onClick={handleCloseDrawer}
                sx={{
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <X className="w-5 h-5" />
              </IconButton>
            </div>
          </div>

          {/* Item Search Field */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <TextField
              fullWidth
              size="small"
              placeholder="Search items by code or description..."
              value={itemSearchTerm}
              onChange={(e) => setItemSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4 text-gray-400" />
                  </InputAdornment>
                ),
                endAdornment: itemSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setItemSearchTerm("")}
                      edge="end"
                    >
                      <X className="w-4 h-4" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  size="small"
                  checked={isAllSelected()}
                  indeterminate={isSomeSelected()}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  sx={{ padding: "4px" }}
                />
                <span className="text-xs text-gray-700 font-medium">
                  Select All
                </span>
              </div>
              <button
                onClick={handleTransfer}
                disabled={!hasAnyChecked}
                className={` px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                  hasAnyChecked
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-200 cursor-not-allowed"
                }`}
                title={
                  hasAnyChecked
                    ? `${checkedCount} items selected`
                    : "No items selected"
                }
              >
                Transfer Items ({checkedCount})
              </button>
            </div>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                <span className="text-sm text-gray-600">Loading items...</span>
              </div>
            ) : filteredDetails.length > 0 ? (
              <div className="space-y-3">
                {filteredDetails.map((item, index) => {
                  const originalIndex = details.indexOf(item);
                  const uniqueItemId = getUniqueItemId(
                    selectedRowData[docNoField],
                    item,
                    originalIndex
                  );
                  const currentQty = itemQuantities[uniqueItemId] || 0;
                  const originalQty = item[itemQtyField] || 0;
                  const qtyDifference = currentQty - originalQty;
                  const newLineTotal = calculateLineTotal(
                    item.unit_price,
                    currentQty
                  );
                  const isChecked = checkedItems[uniqueItemId] || false;

                  return (
                    <div
                      key={uniqueItemId}
                      className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                        isChecked
                          ? "border-blue-300 bg-blue-50/30"
                          : "border-gray-200"
                      }`}
                    >
                      {/* Item Header with Checkbox */}
                      <div className="flex items-start gap-3 mb-3">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxToggle(uniqueItemId)}
                          sx={{ padding: "4px", marginTop: "-4px" }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              {item.code?.trim() || "-"}
                            </span>
                            <span className="text-xs text-gray-500">
                              Line #{originalIndex + 1}
                            </span>
                          </div>
                          <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                            {item.idesc || "-"}
                          </h4>
                        </div>
                      </div>

                      {/* Item Details Grid */}
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1.5">
                            Unit Price
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {item.unit_price?.toFixed(2) || "0.00"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1.5">
                            <span className="inline-flex items-center gap-1">
                              Invoice Qty
                              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                            </span>
                          </p>
                          <p className="text-sm font-semibold text-blue-600">
                            {originalQty.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1.5">
                            <span className="inline-flex items-center gap-1">
                              Return Qty
                              <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                            </span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max={originalQty}
                              // step="0.01"
                              value={currentQty}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                // prevent exceeding invoice qty
                                if (value > originalQty) return;
                                handleQuantityChange(uniqueItemId, value);
                              }}
                              disabled={!isChecked}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="0"
                            />

                            <button
                              onClick={() =>
                                handleQuantityChange(uniqueItemId, originalQty)
                              }
                              disabled={!isChecked}
                              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Max
                            </button>
                          </div>
                        </div>
                        <div className=" flex flex-col justify-center items-end">
                          <span className="text-xs font-medium text-gray-600">
                            Line Total
                          </span>
                          <div className="text-right">
                            <p
                              className={`text-base font-bold ${
                                currentQty > 0
                                  ? "text-blue-600"
                                  : "text-gray-400"
                              }`}
                            >
                              {newLineTotal}
                            </p>
                            {currentQty !== originalQty && originalQty > 0 && (
                              <p className="text-xs text-gray-500 line-through">
                                Original:{" "}
                                {item[itemLineTotalField]?.toFixed(2) || "0.00"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : itemSearchTerm ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Search className="w-12 h-12 mb-3 opacity-40" />
                <p className="text-sm">No items match your search</p>
                <p className="text-xs text-gray-400 mt-1">
                  Try different keywords
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Package className="w-12 h-12 mb-3 opacity-40" />
                <p className="text-sm">No items found</p>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ItemListDrawerSalesReturn;
